
/* eslint-disable import/order -- for now*/
/* eslint-disable max-lines -- for now */
/* eslint-disable @typescript-eslint/no-unnecessary-condition -- for now*/
/* eslint-disable @typescript-eslint/strict-boolean-expressions -- for now*/
// src/store/slices/poc/messagingSlice.ts

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { createLogger, type Logger } from '@/utils/logger';

import { FILE_TRANSFER_TOPIC, HAP_FILE_ANNOUNCE_TOPIC, PUBSUB_TOPICS, SIX_TOPIC } from '@/config/networkConstants';
import type { MessageHandlerResult } from '@/services/abstract/poc/messaging.service.interface';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata } from '@/types/poc/helia';
import type { MessagePayload } from '@/types/poc/message';
import type { AppDispatch } from '@/store';
import type { RootState } from '@/store/rootReducer';
import { addFileMetadata, setUploadProgress, type UploadProgress } from './heliaSlice';
import { enqueueFile } from './cloneQueueSlice';
import { processCloneQueue } from '@/store/thunks/poc/cloneQueue';
import { pollFilesUntilNewFile } from '@/store/thunks/sixDetail/pollFilesUntilNewFile';
import { pollForClonedFiles } from '@/store/thunks/sixDetail/pollForClonedFiles';
import type { HeliaService } from '@/services/abstract/poc/helia.service.interface';
import { parseCid } from '@/services/poc/cidUtils';
import type { P2PStackManager } from '@/services/abstract/poc/p2p.stack-manager.interface';
import { facilitateFileKeys } from '@/store/thunks/fileKey/facilitateFileKeysThunk';

/* -------------------------------------------------------------------------- */
/*  Module-level Logger (for extraReducers)                                   */
/* -------------------------------------------------------------------------- */
const log: Logger = createLogger({ namespace: 'six3ro:p2p:messaging' });

/* -------------------------------------------------------------------------- */
/*  Pending Acknowledgement Tracker                                           */
/* -------------------------------------------------------------------------- */

/** Timeout in milliseconds to wait for acknowledgement */
const ACK_TIMEOUT_MS = 5000;

/** Maximum number of resend attempts */
const MAX_RESEND_ATTEMPTS = 3;

interface PendingAckEntry {
  timeout: ReturnType<typeof setTimeout>;
  params: {
    sixId: string;
    cid: string;
    metadata: { name: string };
    targetPeerId?: string;
  };
  resendFn: () => void;
  attemptCount: number;
}

/** Map of message ID -> pending ack entry with resend capability */
const pendingAcknowledgements = new Map<string, PendingAckEntry>();

/**
 * Track a sent file-ready-for-download message and resend if no ack received within timeout
 */
export function trackPendingAck(
  messageId: string,
  params: { sixId: string; cid: string; metadata: { name: string }; targetPeerId?: string },
  resendFn: () => void,
  attemptCount = 1
): void {
  const { sixId, metadata } = params;

  const timeout = setTimeout(() => {
    pendingAcknowledgements.delete(messageId);

    if (attemptCount >= MAX_RESEND_ATTEMPTS) {
      log.error(`[${sixId}] No acknowledgement received for file ${metadata.name} after ${MAX_RESEND_ATTEMPTS} attempts. Giving up.`);
      // eslint-disable-next-line no-alert --- design tbd
      alert('Something went wrong with your upload. Please refresh and try again.');

      return;
    }

    log.warn(`[${sixId}] No acknowledgement received for file-ready-for-download message ${messageId} (file: ${metadata.name}) within ${ACK_TIMEOUT_MS}ms. Resending (attempt ${attemptCount + 1}/${MAX_RESEND_ATTEMPTS})...`);
    resendFn();
  }, ACK_TIMEOUT_MS);

  pendingAcknowledgements.set(messageId, {
    timeout,
    params,
    resendFn,
    attemptCount
  });
}

/**
 * Clear pending ack tracking when acknowledgement is received
 */
function clearPendingAck(messageId: string): boolean {
  const entry = pendingAcknowledgements.get(messageId);
  if (entry) {
    clearTimeout(entry.timeout);
    pendingAcknowledgements.delete(messageId);
    return true;
  }
  return false;
}

/* -------------------------------------------------------------------------- */
/*  CID Lookup Helper                                                         */
/* -------------------------------------------------------------------------- */

function findUploadEntryByCid(
  uploadProgress: Record<string, UploadProgress>,
  targetCid: string
): { fileName: string; uploadProgress: UploadProgress } | null {
  for (const [fileName, entry] of Object.entries(uploadProgress)) {
    if (entry.cid === targetCid) {
      return { fileName, uploadProgress: entry };
    }
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Silence Watchdog                                                          */
/* -------------------------------------------------------------------------- */

const SILENCE_TIMEOUT_MS = 30_000;
const PROGRESS_HAP_START = 80;
const PROGRESS_HAP_RANGE = 10;
const PROGRESS_HAP_REGISTERING = 90;

const silenceWatchdogTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function armSilenceWatchdog(
  cid: string,
  dispatch: AppDispatch,
  getState: () => RootState
): void {
  const existing = silenceWatchdogTimers.get(cid);
  if (existing) {
    clearTimeout(existing);
  }

  const timer = setTimeout(() => {
    silenceWatchdogTimers.delete(cid);
    const currentUploadProgress = getState().helia.uploadProgress;
    const match = findUploadEntryByCid(currentUploadProgress, cid);
    if (match && match.uploadProgress.stage !== 'complete') {
      dispatch(setUploadProgress({
        fileName: match.fileName,
        stage: 'error',
        progress: match.uploadProgress.progress,
        error: 'Lost data connection to HAP — your upload may still be processing. Try refreshing.'
      }));
    }
  }, SILENCE_TIMEOUT_MS);

  silenceWatchdogTimers.set(cid, timer);
}

export function clearSilenceWatchdog(cid: string): void {
  const existing = silenceWatchdogTimers.get(cid);
  if (existing) {
    clearTimeout(existing);
    silenceWatchdogTimers.delete(cid);
  }
}

/**
 * Clear all module-level timers. Called during logout to prevent orphaned
 * setTimeout handles from firing after the app has torn down.
 */
export function clearAllModuleTimers(): void {
  for (const entry of pendingAcknowledgements.values()) {
    clearTimeout(entry.timeout);
  }
  pendingAcknowledgements.clear();

  for (const timer of silenceWatchdogTimers.values()) {
    clearTimeout(timer);
  }
  silenceWatchdogTimers.clear();

  for (const timer of pendingFileListRefreshes.values()) {
    clearTimeout(timer);
  }
  pendingFileListRefreshes.clear();
}

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface SixMessagingState {
  sixId: string;
  peerFileRegistry: Record<string, string[]>;
  fileMetadata: Record<string, FileMetadata>;
  recentMessages: MessageHandlerResult[];
  maxRecentMessages: number;
  status: 'idle' | 'initializing' | 'ready' | 'error' | 'cleaning-up';
  error: string | null;
  stats: {
    totalMessagesReceived: number;
    totalMessagesSent: number;
    totalPeersDiscovered: number;
    totalFilesAnnounced: number;
    totalFilesRequested: number;
    totalFilesReplicated: number;
  };
}

export interface MessagingState {
  sixMessaging: SixMessagingState[];
}

interface FileUploadedData {
  cid: string;
  metadata: FileMetadata;
  sender: string;
  shouldReplicate: boolean;
}

interface FilesUpdatedData {
  files: Array<{
    cid: string;
    metadata: FileMetadata;
    shouldReplicate: boolean;
  }>;
  sender: string;
}

/* -------------------------------------------------------------------------- */
/*  Pure Helper Functions                                                     */
/* -------------------------------------------------------------------------- */

const createInitialSixMessaging = (sixId: string): SixMessagingState => ({
  sixId,
  peerFileRegistry: {},
  fileMetadata: {},
  recentMessages: [],
  maxRecentMessages: 50,
  status: 'idle',
  error: null,
  stats: {
    totalMessagesReceived: 0,
    totalMessagesSent: 0,
    totalPeersDiscovered: 0,
    totalFilesAnnounced: 0,
    totalFilesRequested: 0,
    totalFilesReplicated: 0,
  },
});

const findSixMessaging = (
  sixMessagingArray: SixMessagingState[],
  sixId: string
): SixMessagingState | undefined =>
  sixMessagingArray.find(sixMessaging => sixMessaging.sixId === sixId);

const ensureSixMessaging = (
  sixMessagingArray: SixMessagingState[],
  sixId: string
): SixMessagingState[] =>
  findSixMessaging(sixMessagingArray, sixId)
    ? sixMessagingArray
    : [...sixMessagingArray, createInitialSixMessaging(sixId)];

const updateSixMessaging = (
  sixMessagingArray: SixMessagingState[],
  sixId: string,
  updater: (sixMessaging: SixMessagingState) => SixMessagingState
): SixMessagingState[] =>
  sixMessagingArray.map(sixMessaging =>
    sixMessaging.sixId === sixId ? updater(sixMessaging) : sixMessaging
  );

const removeSixMessaging = (
  sixMessagingArray: SixMessagingState[],
  sixId: string
): SixMessagingState[] =>
  sixMessagingArray.filter(sixMessaging => sixMessaging.sixId !== sixId);

/* -------------------------------------------------------------------------- */
/*  Initial State                                                             */
/* -------------------------------------------------------------------------- */

const initialState: MessagingState = {
  sixMessaging: [],
};

/* -------------------------------------------------------------------------- */
/*  Message Handling Thunk                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Single entry point for all incoming message processing.
 * Now requires sixId to route to correct six's messaging state.
 */
export const handleIncomingMessage = createAppAsyncThunk(
  'messaging/handleIncomingMessage',
  async(
    payload: MessageHandlerResult & { sixId: string },
    { dispatch, getState, extra: { pocServices, loggerService } }
  ) => {
    const thunkLog = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
    const { sixId, ...result } = payload;

    switch (result.type) {
      case 'file-ready-for-download': {
        await handleFileUploadedAsync(
          sixId,
          result.data as FileUploadedData,
          dispatch,
          { pocServices, logger: thunkLog }
        );
        break;
      }

      case 'files-updated': {
        handleFilesUpdatedAsync(
          sixId,
          result.data as FilesUpdatedData,
          dispatch,
          { getState, logger: thunkLog }
        );
        break;
      }

      case 'file-cloned': {
        const { cid } = result.data;
        thunkLog.info(`[${sixId}] File cloned message received for CID: ${cid}`);

        clearSilenceWatchdog(cid);

        const currentUploadProgress = getState().helia.uploadProgress;
        const uploadEntry = Object.values(currentUploadProgress).find(
          (progress) => progress.cid === cid
        );
        if (uploadEntry !== undefined) {
          dispatch(setUploadProgress({
            fileName: uploadEntry.fileName,
            cid,
            stage: 'complete',
            progress: 100
          }));
        }

        void dispatch(pollForClonedFiles({ sixId, cid }));
        break;
      }

      case 'file-replication-started': {
        const { cid: startedCid, totalBytes } = result.data;
        const startedMatch = findUploadEntryByCid(getState().helia.uploadProgress, startedCid);
        if (!startedMatch) {
          thunkLog.debug(`[${sixId}] file-replication-started for unknown CID: ${startedCid}`);
          break;
        }
        dispatch(setUploadProgress({
          fileName: startedMatch.fileName,
          stage: 'hap-downloading',
          progress: PROGRESS_HAP_START,
          hapBytesTransferred: 0,
          hapTotalBytes: totalBytes
        }));
        armSilenceWatchdog(startedCid, dispatch, getState);
        break;
      }

      case 'file-replication-progress': {
        const { cid: progressCid, stage: replicationStage, bytesTransferred, totalBytes: progressTotalBytes } = result.data;
        let progressMatch = findUploadEntryByCid(getState().helia.uploadProgress, progressCid);

        if (!progressMatch) {
          const announcingMatch = Object.entries(getState().helia.uploadProgress).find(
            ([, entry]) => entry.cid === progressCid && entry.stage === 'announcing'
          );
          if (announcingMatch) {
            const [announcingFileName] = announcingMatch;
            dispatch(setUploadProgress({
              fileName: announcingFileName,
              stage: 'hap-downloading',
              progress: PROGRESS_HAP_START,
              hapBytesTransferred: 0,
              hapTotalBytes: progressTotalBytes
            }));
            progressMatch = findUploadEntryByCid(getState().helia.uploadProgress, progressCid);
          }
        }

        if (!progressMatch) {
          thunkLog.debug(`[${sixId}] file-replication-progress for unknown CID: ${progressCid}`);
          break;
        }

        if (replicationStage === 'fetching') {
          const computedProgress = Math.min(
            PROGRESS_HAP_START + (progressTotalBytes > 0 ? (bytesTransferred / progressTotalBytes) * PROGRESS_HAP_RANGE : PROGRESS_HAP_RANGE),
            PROGRESS_HAP_REGISTERING
          );
          dispatch(setUploadProgress({
            fileName: progressMatch.fileName,
            stage: 'hap-downloading',
            progress: computedProgress,
            hapBytesTransferred: bytesTransferred
          }));
        } else if (replicationStage === 'registering') {
          dispatch(setUploadProgress({
            fileName: progressMatch.fileName,
            stage: 'hap-registering',
            progress: PROGRESS_HAP_REGISTERING
          }));
        }

        armSilenceWatchdog(progressCid, dispatch, getState);
        break;
      }

      case 'file-replication-failed': {
        const { cid: failedCid, reason } = result.data;
        const failedMatch = findUploadEntryByCid(getState().helia.uploadProgress, failedCid);
        if (!failedMatch) {
          thunkLog.debug(`[${sixId}] file-replication-failed for unknown CID: ${failedCid}`);
          break;
        }
        dispatch(setUploadProgress({
          fileName: failedMatch.fileName,
          stage: 'error',
          progress: failedMatch.uploadProgress.progress,
          error: reason
        }));
        clearSilenceWatchdog(failedCid);
        break;
      }

      case 'file-ready-for-download-acknowledged': {
        const { messageId, sender } = result.data;
        const wasTracked = clearPendingAck(messageId);
        if (wasTracked) {
          thunkLog.info(`[${sixId}] Received acknowledgement for message ${messageId} from ${sender}`);
        } else {
          thunkLog.debug(`[${sixId}] Received acknowledgement for unknown/expired message ${messageId}`);
        }
        break;
      }

      case 'file-key-ready-for-download-acknowledged': {
        const { messageId: fkMessageId, sender: fkSender } = result.data;
        const fkWasTracked = clearPendingAck(fkMessageId);
        if (fkWasTracked) {
          thunkLog.info(`[${sixId}] Received file-key acknowledgement for message ${fkMessageId} from ${fkSender}`);
        } else {
          thunkLog.debug(`[${sixId}] Received file-key acknowledgement for unknown/expired message ${fkMessageId}`);
        }
        break;
      }

      case 'check-for-keys-needing-regenerating': {
        const { sixId: affectedSixId, sender: hapSender } = result.data;
        thunkLog.info(`[${sixId}] Received check-for-keys-needing-regenerating for six ${affectedSixId} from ${hapSender}`);

        const facilitationStatus = getState().fileKeyFacilitation.status;
        if (facilitationStatus === 'running') {
          thunkLog.info(`[${sixId}] File key facilitation already running, skipping`);
        } else {
          void dispatch(facilitateFileKeys());
        }
        break;
      }
    }

    return { sixId, type: result.type, processed: true, result };
  }
);

/* -------------------------------------------------------------------------- */
/*  Message Type Handlers (Private)                                          */
/* -------------------------------------------------------------------------- */

/**
 * Handle file-ready-for-download message with local availability check for specific six
 */
async function handleFileUploadedAsync(
  sixId: string,
  data: FileUploadedData,
  dispatch: AppDispatch,
  options: {
    pocServices: { p2pStackManagerService: P2PStackManager; heliaService: HeliaService };
    logger: Logger;
  }
): Promise<void> {
  const { pocServices, logger } = options;
  const { cid, metadata, sender } = data;
  const { p2pStackManagerService: p2pStackManager, heliaService } = pocServices;

  if (cid === undefined || cid === null) {
    throw new Error('Invalid file announcement: CID is undefined or null');
  }

  logger.info(`[${sixId}] File announcement: ${cid} from ${sender}`);

  // Always store metadata and peer info for this six
  dispatch(storeFileMetadata({ sixId, cid, metadata }));
  dispatch(markPeerHasFile({ sixId, peerId: sender, cid }));

  // Poll for the file to appear in the API
  void dispatch(pollFilesUntilNewFile({ sixId }));

  // Check if we have it locally in this six's Helia node
  const heliaNode = p2pStackManager.getHeliaNode(sixId);
  if (heliaNode === null) {
    return;
  }

  try {
    const fileExists = await heliaService.fileExists(heliaNode, parseCid(cid));

    if (fileExists) {
      logger.info(`[${sixId}] File ${cid} already exists locally`);
      dispatch(addFileMetadata(metadata));
    } else {
      logger.info(`[${sixId}] File ${cid} not local, adding to clone queue`);
      dispatch(enqueueFile({
        sixId,
        cid,
        metadata,
        size: metadata.size ?? 0,
        priority: 'normal'
      }));
      void dispatch(processCloneQueue());
    }
  } catch (error) {
    logger.error(`[${sixId}] Error checking file existence for ${cid}:`, error);
  }
}

/* -------------------------------------------------------------------------- */
/*  File List Debouncing                                                       */
/* -------------------------------------------------------------------------- */

/** Map of sixId -> pending refresh timeout */
const pendingFileListRefreshes = new Map<string, ReturnType<typeof setTimeout>>();

/** Debounce delay in milliseconds */
const FILE_LIST_DEBOUNCE_MS = 500;

/**
 * Handle files-updated P2P message by refreshing files from the API.
 * Uses debouncing to coalesce rapid messages into a single API call.
 */
function handleFilesUpdatedAsync(
  sixId: string,
  data: FilesUpdatedData,
  dispatch: AppDispatch,
  options: { getState: () => RootState; logger: Logger }
): void {
  const { getState, logger } = options;
  logger.info(`[${sixId}] File list message received, will refresh from API`);

  const { files, sender } = data;

  // 1. Mark peer ownership (still useful for P2P coordination)
  if (sender && files && Array.isArray(files)) {
    for (const file of files) {
      if (file.cid) {
        dispatch(markPeerHasFile({ sixId, peerId: sender, cid: file.cid }));
      }
    }
  }

  // 2. Check upload progress completion
  const state = getState();
  const currentUploadProgress = state.helia.uploadProgress;
  const PROGRESS_COMPLETE = 100;

  if (files && Array.isArray(files)) {
    for (const file of files) {
      const uploadEntry = Object.values(currentUploadProgress).find(
        (progress) => progress.cid === file.cid
      );
      if (uploadEntry !== undefined) {
        logger.info(`Upload complete for file: ${uploadEntry.fileName} (CID: ${file.cid})`);
        dispatch(setUploadProgress({
          fileName: uploadEntry.fileName,
          cid: file.cid,
          stage: 'complete',
          progress: PROGRESS_COMPLETE
        }));
      }
    }
  }

  // 3. Debounced poll from API (source of truth)
  const existingTimeout = pendingFileListRefreshes.get(sixId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeout = setTimeout(() => {
    logger.debug(`[${sixId}] Debounce complete, polling files from API`);
    void dispatch(pollFilesUntilNewFile({ sixId }));
    pendingFileListRefreshes.delete(sixId);
  }, FILE_LIST_DEBOUNCE_MS);

  pendingFileListRefreshes.set(sixId, timeout);
}

/* -------------------------------------------------------------------------- */
/*  Messaging Operations                                                      */
/* -------------------------------------------------------------------------- */

export const sendFileAnnouncement = createAppAsyncThunk(
  'messaging/sendFileAnnouncement',
  async(
    {
      sixId,
      cid,
      metadata,
      targetPeerId,
      attemptCount = 1,
    }: {
      sixId: string;
      cid: string;
      metadata: FileMetadata;
      targetPeerId?: string;
      attemptCount?: number;
    },
    { dispatch, extra: { pocServices, loggerService } }
  ) => {
    const thunkLog = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
    const { p2pStackManagerService, messagingService } = pocServices;
    const libp2pNode = p2pStackManagerService.getLibp2pNode(sixId);

    if (!libp2pNode) {
      throw new Error(`LibP2P node not available for six '${sixId}'`);
    }

    const message: MessagePayload = {
      type: 'file-ready-for-download',
      id: crypto.randomUUID(),
      cid,
      metadata,
      targetPeerId,
      sender: libp2pNode.peerId.toString(),
      timestamp: Date.now(),
    };

    await messagingService.publishMessage(
      libp2pNode,
      SIX_TOPIC(sixId),
      message
    );

    // Track pending acknowledgement - resend if no ack within timeout
    const params = { sixId, cid, metadata, targetPeerId };
    trackPendingAck(
      message.id,
      params,
      () => { void dispatch(sendFileAnnouncement({ ...params, attemptCount: attemptCount + 1 })); },
      attemptCount
    );

    thunkLog.info(`[${sixId}] File ready for download message ${message.id} sent (attempt ${attemptCount}/${MAX_RESEND_ATTEMPTS}) for file: ${metadata.name}`);
    return { sixId, cid, messageId: message.id, sent: true, usedProtocol: false, attemptCount };
  });

export const cleanupSixMessaging = createAppAsyncThunk(
  'messaging/cleanupSix',
  ({ sixId }: { sixId: string }, { dispatch, extra: { loggerService } }) => {
    const thunkLog = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
    thunkLog.info(`[${sixId}] Cleaning up messaging state`);
    dispatch(removeSixMessagingState(sixId));
    return { sixId, cleaned: true };
  }
);

/* -------------------------------------------------------------------------- */
/*  Slice Definition                                                          */
/* -------------------------------------------------------------------------- */

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    markPeerHasFile: (
      state,
      action: PayloadAction<{ sixId: string; peerId: string; cid: string }>
    ) => {
      const { sixId, peerId, cid } = action.payload;

      state.sixMessaging = ensureSixMessaging(state.sixMessaging, sixId);
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => {
        const updatedRegistry = { ...sixMessaging.peerFileRegistry };

        if (!updatedRegistry[peerId]) {
          updatedRegistry[peerId] = [];
          return {
            ...sixMessaging,
            peerFileRegistry: updatedRegistry,
            stats: {
              ...sixMessaging.stats,
              totalPeersDiscovered: sixMessaging.stats.totalPeersDiscovered + 1,
            },
          };
        }

        if (!updatedRegistry[peerId].includes(cid)) {
          updatedRegistry[peerId] = [...updatedRegistry[peerId], cid];
        }

        return {
          ...sixMessaging,
          peerFileRegistry: updatedRegistry,
        };
      });
    },

    storeFileMetadata: (
      state,
      action: PayloadAction<{ sixId: string; cid: string; metadata: FileMetadata }>
    ) => {
      const { sixId, cid, metadata } = action.payload;

      state.sixMessaging = ensureSixMessaging(state.sixMessaging, sixId);
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => {
        const hadMetadata = !!sixMessaging.fileMetadata[cid];

        return {
          ...sixMessaging,
          fileMetadata: {
            ...sixMessaging.fileMetadata,
            [cid]: metadata,
          },
          stats: {
            ...sixMessaging.stats,
            totalFilesAnnounced: hadMetadata
              ? sixMessaging.stats.totalFilesAnnounced
              : sixMessaging.stats.totalFilesAnnounced + 1,
          },
        };
      });
    },

    storeMultipleFileMetadata: (
      state,
      action: PayloadAction<{
        sixId: string;
        entries: Array<{ cid: string; metadata: FileMetadata }>;
      }>
    ) => {
      const { sixId, entries } = action.payload;

      state.sixMessaging = ensureSixMessaging(state.sixMessaging, sixId);
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => {
        const updatedMetadata = { ...sixMessaging.fileMetadata };
        let newFilesCount = 0;

        entries.forEach(({ cid, metadata }) => {
          if (!updatedMetadata[cid]) {
            newFilesCount += 1;
          }
          updatedMetadata[cid] = metadata;
        });

        return {
          ...sixMessaging,
          fileMetadata: updatedMetadata,
          stats: {
            ...sixMessaging.stats,
            totalFilesAnnounced: sixMessaging.stats.totalFilesAnnounced + newFilesCount,
          },
        };
      });
    },

    logMessage: (
      state,
      action: PayloadAction<{ sixId: string; message: MessageHandlerResult }>
    ) => {
      const { sixId, message } = action.payload;

      state.sixMessaging = ensureSixMessaging(state.sixMessaging, sixId);
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => {
        const updatedMessages = [message, ...sixMessaging.recentMessages];

        return {
          ...sixMessaging,
          recentMessages: updatedMessages.slice(0, sixMessaging.maxRecentMessages),
          stats: {
            ...sixMessaging.stats,
            totalMessagesReceived: sixMessaging.stats.totalMessagesReceived + 1,
          },
        };
      });
    },

    resetAllMessaging: () => initialState,

    removeSixMessagingState: (state, action: PayloadAction<string>) => {
      state.sixMessaging = removeSixMessaging(state.sixMessaging, action.payload);
    },

    clearPeerRegistry: (state, action: PayloadAction<string>) => {
      const sixId = action.payload;
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => ({
        ...sixMessaging,
        peerFileRegistry: {},
        stats: {
          ...sixMessaging.stats,
          totalPeersDiscovered: 0,
        },
      }));
    },

    setMessagingError: (
      state,
      action: PayloadAction<{ sixId: string; error: string }>
    ) => {
      const { sixId, error } = action.payload;
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => ({
        ...sixMessaging,
        error,
        status: 'error',
      }));
    },

    clearMessagingError: (state, action: PayloadAction<string>) => {
      const sixId = action.payload;
      state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => ({
        ...sixMessaging,
        error: null,
        status: sixMessaging.status === 'error' ? 'idle' : sixMessaging.status,
      }));
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(handleIncomingMessage.fulfilled, (state, action) => {
        const { sixId, result } = action.payload;
        state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => {
          const updatedMessages = [...sixMessaging.recentMessages, result];
          return {
            ...sixMessaging,
            recentMessages: updatedMessages,
          };
        });
      })
      .addCase(handleIncomingMessage.rejected, (state, action) => {
        const sixId = action.meta.arg.sixId;
        state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => ({
          ...sixMessaging,
          error: action.error.message ?? 'Failed to handle incoming message',
        }));
      })

      .addCase(sendFileAnnouncement.fulfilled, (state, action) => {
        const { sixId } = action.payload;
        state.sixMessaging = updateSixMessaging(state.sixMessaging, sixId, sixMessaging => ({
          ...sixMessaging,
          stats: {
            ...sixMessaging.stats,
            totalMessagesSent: sixMessaging.stats.totalMessagesSent + 1,
          },
        }));
      });
  },
});

/* -------------------------------------------------------------------------- */
/*  Actions Export                                                            */
/* -------------------------------------------------------------------------- */

export const {
  markPeerHasFile,
  storeFileMetadata,
  storeMultipleFileMetadata,
  logMessage,
  resetAllMessaging,
  removeSixMessagingState,
  clearPeerRegistry,
  setMessagingError,
  clearMessagingError,
} = messagingSlice.actions;

/* -------------------------------------------------------------------------- */
/*  Pure Selectors                                                            */
/* -------------------------------------------------------------------------- */

export const selectSixMessaging = (state: { messaging: MessagingState }, sixId: string) =>
  findSixMessaging(state.messaging.sixMessaging, sixId) ?? createInitialSixMessaging(sixId);

export const selectAllSixMessaging = (state: { messaging: MessagingState }) =>
  state.messaging.sixMessaging;

export const selectSixPeerFileRegistry = (state: { messaging: MessagingState }, sixId: string) =>
  selectSixMessaging(state, sixId).peerFileRegistry;

export const selectSixFileMetadata = (state: { messaging: MessagingState }, sixId: string) =>
  selectSixMessaging(state, sixId).fileMetadata;

export const selectSixRecentMessages = (state: { messaging: MessagingState }, sixId: string) =>
  selectSixMessaging(state, sixId).recentMessages;

export const selectSixMessagingStats = (state: { messaging: MessagingState }, sixId: string) =>
  selectSixMessaging(state, sixId).stats;

export const selectAllSixIds = (state: { messaging: MessagingState }) =>
  state.messaging.sixMessaging.map(sixMessaging => sixMessaging.sixId);

/* -------------------------------------------------------------------------- */
/*  Reducer Export                                                            */
/* -------------------------------------------------------------------------- */

export default messagingSlice.reducer;
